
/* ================================================================
   LUMA - DIAMOND MEDIA VIEWER + LET LUMA CHOOSE
   Added 6 October 2026.

   WHY THIS EXISTS
   Asher, 6 Oct: "We've had a couple of customers get the wrong
   diamond and I want them to be able to see it better."

   The cause was not missing data. The proxy was already returning
   diamond.image and diamond.video for nearly every stone. The card
   rendered the photo into a 56x56 box with object-fit: cover, and
   read diamond.video only to decide whether to draw a decorative
   play triangle. Nothing listened for a click on it and the video
   URL was discarded. Measured 6 Oct: the still is 713x564, so
   customers were judging a stone from about 1% of the photo that
   had already been downloaded for them. Across 300 stones in 10
   shapes, 295 had a 360.

   WHAT IT DOES
   1. Enlarges the thumbnail and stops the square crop.
   2. Click the thumbnail to open a media panel inside the card,
      with the full photo and, where the stone has one, a 360 tab.
   3. The 360 iframe is created only when that tab is clicked, so a
      list of 30 stones never loads 30 iframes.
   4. Wires Let Luma Choose, and raises a stronger prompt for it
      when a search comes back with nothing recommended.

   THE TRAPS, ALL THREE ALREADY PAID FOR IN THIS FILE
   a. Any element hidden with `hidden` that also gets a display
      value needs an explicit [hidden] { display: none !important },
      or the class selector beats the user agent rule and every
      panel is permanently open. This cost a v1 in August.
   b. The panel is a child of the CARD, not of __diamond-info. The
      card is display:flex with align-items:center, so a tall info
      column floats the price into the middle of the panel.
   c. The card's own click handler selects the diamond. Everything
      in here calls stopPropagation, otherwise looking at a stone
      silently buys a different one than the customer thinks.

   THE 360 IS FRAMED, NOT LINKED. Asher on the IGI report number,
   31 Aug: "I don't want them leaving our site". loupe360 embeds
   cleanly, verified on the Astrid page 6 Oct. Do not turn this
   into an outbound link.

   If anything in here throws, the configurator carries on exactly
   as it does today. Nothing below may block a search or an order.
   ================================================================ */
(function () {
  var root = document.getElementById('luma-config');
  if (!root) return;

  var API = root.getAttribute('data-diamond-api') || '';
  var STYLE_ID = 'luma-media-styles';
  var OPEN_CLASS = 'luma-media-open';
  var media = {};   // diamond id -> { image, video }

  /* ---- 1. capture media from the search response ---------------- */
  var nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = (typeof input === 'string') ? input : (input && input.url) || '';
    var p = nativeFetch.call(this, input, init);
    if (API && url.indexOf(API) === 0) {
      try {
        p.then(function (res) {
          res.clone().json().then(function (j) {
            var items = (j && j.data && j.data.as && j.data.as.diamonds_by_query &&
                         j.data.as.diamonds_by_query.items) || [];
            media = {};
            items.forEach(function (d) {
              if (d && d.id && d.diamond) {
                media[String(d.id)] = {
                  image: d.diamond.image || '',
                  video: d.diamond.video || ''
                };
              }
            });
            // The cards may already be on the page by now. tick() is hoisted.
            tick();
          }).catch(function () {});
        }).catch(function () {});
      } catch (e) { /* never interfere with the search */ }
    }
    return p;
  };

  /* ---- 2. the 360 carries its own render size in the path -------
     .../<id>/<id>/video/500/500 .

     THIS IS NOT A QUALITY DIAL, IT IS THE VIEWER'S CANVAS SIZE IN CSS PIXELS.
     Two wrong answers already, both for the same reason.

     v1 asked for 1000x1000 on the theory that bigger is sharper. It is not.
     loupe360 lays the viewer out at exactly the size requested, so a 1000px
     viewer in a 268px frame scrolls and you see a grey corner of the stone.

     v2 asked for the frame size multiplied by devicePixelRatio, for crispness.
     Same mistake in smaller clothing: on a 2x screen that asks for a 536px
     viewer in a 268px frame. Even at 1x it overflowed, because the viewer page
     carries the default 8px body margin, so 270 requested became 286 laid out
     in a 268 frame. That is the slight scroll Asher reported, 6 Oct 2026.

     So: CSS pixels, never device pixels, and 16px short of the frame to leave
     room for that body margin. The canvas then sits centred with nothing to
     scroll. scrolling="no" on the iframe is belt and braces.            */
  function sizeViewer(u, px) {
    try { return u.replace(/\/video\/\d+\/\d+/, '/video/' + px + '/' + px); }
    catch (e) { return u; }
  }

  function closeAll(except) {
    var open = root.querySelectorAll('.' + OPEN_CLASS);
    for (var i = 0; i < open.length; i++) {
      if (open[i] === except) continue;
      open[i].classList.remove(OPEN_CLASS);
      var pnl = open[i].querySelector('.luma-media__panel');
      if (pnl) pnl.hidden = true;
    }
  }

  // .luma-config__diamonds-grid is max-height 400px with overflow-y: auto, so
  // a card that grows by a 268px media panel is taller than the box it lives
  // in and the customer sees the top third of the stone. Give the list room
  // while something is open, and take it back when nothing is.
  function syncGridHeight() {
    var grid = root.querySelector('.luma-config__diamonds-grid');
    if (!grid) return;
    grid.classList.toggle('luma-media-expanded', !!root.querySelector('.' + OPEN_CLASS));
  }

  /* ---- 3. decorate one card ------------------------------------- */
  function decorate(card) {
    if (card.getAttribute('data-luma-media-done') === '1') return;

    var id = String(card.getAttribute('data-diamond-id') || '');
    var m = media[id] || {};
    var box = card.querySelector('.luma-config__diamond-media');
    // DO NOT mark the card done before this check. The media map is filled
    // from a cloned response, two microtasks deep, and the configurator
    // renders its cards from the same response. The order is not guaranteed.
    // Marking first meant that on an unlucky ordering every card was flagged
    // done while media was still empty and the viewer never appeared at all.
    // Reproduced 6 Oct 2026: 0 of 30 decorated. Marking only on success, plus
    // the tick() in the fetch hook below, makes it work either way round.
    if (!box || (!m.image && !m.video)) return;
    card.setAttribute('data-luma-media-done', '1');

    // The play badge is pointer-events: none in the theme CSS and only
    // appears on hover. Make the whole thumbnail the control instead,
    // so it works on touch, where there is no hover at all.
    box.setAttribute('role', 'button');
    box.setAttribute('tabindex', '0');
    box.setAttribute('aria-label', m.video ? 'View this diamond larger and in 360' : 'View this diamond larger');
    box.classList.add('luma-media__trigger');
    if (m.video) box.classList.add('luma-media__trigger--has360');

    var panel = document.createElement('div');
    panel.className = 'luma-media__panel';
    panel.hidden = true;

    var tabs = document.createElement('div');
    tabs.className = 'luma-media__tabs';

    var stage = document.createElement('div');
    stage.className = 'luma-media__stage';

    var photo = document.createElement('img');
    photo.className = 'luma-media__photo';
    photo.alt = 'Diamond photograph';
    photo.loading = 'lazy';
    if (m.image) photo.src = m.image;
    stage.appendChild(photo);

    var frameWrap = document.createElement('div');
    frameWrap.className = 'luma-media__360';
    frameWrap.hidden = true;
    stage.appendChild(frameWrap);

    function mkTab(label, name) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'luma-media__tab';
      b.textContent = label;
      b.setAttribute('data-view', name);
      return b;
    }

    var tabPhoto = mkTab('Photo', 'photo');
    var tab360 = m.video ? mkTab('360 view', '360') : null;
    tabPhoto.classList.add('is-active');
    tabs.appendChild(tabPhoto);
    if (tab360) tabs.appendChild(tab360);

    function show(view) {
      var is360 = view === '360';
      photo.hidden = is360;
      frameWrap.hidden = !is360;
      tabPhoto.classList.toggle('is-active', !is360);
      if (tab360) tab360.classList.toggle('is-active', is360);
      // Build the iframe on first use only. 30 cards must never mean
      // 30 iframes.
      if (is360 && m.video && !frameWrap.firstChild) {
        var w = Math.round(stage.getBoundingClientRect().width) || 420;
        var px = Math.max(200, Math.min(900, w - 16));
        var f = document.createElement('iframe');
        f.className = 'luma-media__frame';
        f.setAttribute('title', '360 view of this diamond');
        f.setAttribute('loading', 'lazy');
        f.setAttribute('scrolling', 'no');
        f.setAttribute('referrerpolicy', 'no-referrer');
        f.src = sizeViewer(m.video, px);
        frameWrap.appendChild(f);
      }
    }

    tabPhoto.addEventListener('click', function (e) { e.stopPropagation(); show('photo'); });
    if (tab360) tab360.addEventListener('click', function (e) { e.stopPropagation(); show('360'); });

    var hint = document.createElement('p');
    hint.className = 'luma-media__hint';
    hint.textContent = m.video
      ? 'Drag the 360 to turn the stone. This is the stone you would receive, not a stock image.'
      : 'This is the stone you would receive, not a stock image.';

    panel.appendChild(tabs);
    panel.appendChild(stage);
    panel.appendChild(hint);

    // Clicks inside the panel must never reach the card's select handler.
    panel.addEventListener('click', function (e) { e.stopPropagation(); });

    card.appendChild(panel);

    function toggle(e) {
      e.stopPropagation();
      e.preventDefault();
      var isOpen = card.classList.contains(OPEN_CLASS);
      closeAll(card);
      if (isOpen) {
        card.classList.remove(OPEN_CLASS);
        panel.hidden = true;
      } else {
        card.classList.add(OPEN_CLASS);
        panel.hidden = false;
        show('photo');
      }
      syncGridHeight();
      if (card.classList.contains(OPEN_CLASS)) {
        setTimeout(function () {
          try { panel.scrollIntoView({ block: 'nearest' }); } catch (e) {}
        }, 60);
      }
    }

    box.addEventListener('click', toggle);
    box.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') toggle(e);
    });
  }

  /* ---- 4. Let Luma choose --------------------------------------- */
  function syncChoose() {
    var open = document.getElementById('luma-choose-open');
    var panel = document.getElementById('luma-choose-panel');
    if (!open || !panel || open.getAttribute('data-wired') === '1') return;
    open.setAttribute('data-wired', '1');

    open.addEventListener('click', function () {
      var isOpen = open.getAttribute('aria-expanded') === 'true';
      open.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      panel.hidden = isOpen;
      if (!isOpen) prefill();
    });
  }

  // Read what the customer has already chosen, so they do not retype it.
  // Deliberately reads the DOM rather than the configurator's private
  // state object, which is not exposed outside its own closure.
  function prefill() {
    try {
      var shapeBtn = root.querySelector('.luma-config__shape-btn.is-selected');
      var carats = Array.prototype.slice
        .call(root.querySelectorAll('.luma-config__carat-pill.is-selected'))
        .map(function (p) { return p.getAttribute('data-carat'); })
        .filter(Boolean);
      var metalEl = root.querySelector('[data-display-metal]');

      var s = document.getElementById('luma-choose-shape');
      var c = document.getElementById('luma-choose-carat');
      var m = document.getElementById('luma-choose-metal');

      if (s) s.value = shapeBtn ? (shapeBtn.getAttribute('data-shape') || '') : '';
      if (c) c.value = carats.join(', ');
      if (m) m.value = metalEl ? (metalEl.textContent || '').trim() : '';
    } catch (e) { /* an unprefilled form is still a working form */ }
  }

  // Second, stronger prompt. Only when a search actually returned stones
  // and not one of them carries the badge, which is what asscher does
  // today. Never shown on an empty result, which already has its own
  // message, and never shown twice.
  function maybePromptNoRecommended() {
    var results = document.getElementById('luma-diamonds-results');
    if (!results) return;
    var cards = results.querySelectorAll('.luma-config__diamond-card');
    var existing = results.querySelector('.luma-media__norec');
    if (!cards.length) { if (existing) existing.remove(); return; }
    var anyRec = results.querySelector('.luma-config__diamond-card[data-luma-shortlist="true"]');
    if (anyRec) { if (existing) existing.remove(); return; }
    if (existing) return;

    var note = document.createElement('div');
    note.className = 'luma-media__norec';
    var p = document.createElement('p');
    p.textContent = 'None of these stones sits inside the proportion ranges we work to for this shape. ' +
                    'They are all certified and perfectly good stones, we just have not put our name to any of them.';
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'luma-media__norec-btn';
    b.textContent = 'Have our gemologist pick for you';
    b.addEventListener('click', function () {
      var open = document.getElementById('luma-choose-open');
      var panel = document.getElementById('luma-choose-panel');
      if (!open || !panel) return;
      open.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      prefill();
      panel.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    note.appendChild(p);
    note.appendChild(b);
    results.insertBefore(note, results.firstChild);
  }

  /* ---- 5. styles ------------------------------------------------- */
  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    var css = [
      // Bigger, and contain rather than cover so the stone is not clipped
      // before anyone has a chance to look at it.
      '.luma-config__diamond-media.luma-media__trigger {',
      '  width: 84px;',
      '  height: 84px;',
      '  cursor: zoom-in;',
      '}',
      '.luma-config__diamond-media.luma-media__trigger img {',
      '  object-fit: contain;',
      '  background: #F8F6F1;',
      '}',
      '.luma-config__diamond-media.luma-media__trigger:focus-visible {',
      '  outline: 2px solid #C9A456;',
      '  outline-offset: 2px;',
      '}',
      // A persistent affordance. The theme's play badge only appears on
      // hover, which does not exist on a phone.
      '.luma-media__trigger--has360::after {',
      '  content: "360";',
      '  position: absolute;',
      '  right: 3px;',
      '  bottom: 3px;',
      '  font-size: 8px;',
      '  letter-spacing: 0.06em;',
      '  padding: 1px 4px;',
      '  border-radius: 2px;',
      '  background: rgba(26,26,26,0.72);',
      '  color: #fff;',
      '  pointer-events: none;',
      '}',
      '.luma-config__diamond-card.' + OPEN_CLASS + ' { flex-wrap: wrap; }',
      '.luma-config__diamonds-grid.luma-media-expanded { max-height: 70vh; }',
      '.luma-media__panel {',
      '  flex: 0 0 100%;',
      '  width: 100%;',
      '  box-sizing: border-box;',
      '  margin-top: 12px;',
      '  padding: 12px;',
      '  background: #F8F6F1;',
      '  border: 1px solid #ece7dc;',
      '  border-radius: 4px;',
      '  cursor: auto;',
      '  user-select: text;',
      '  -webkit-user-select: text;',
      '}',
      // !! DO NOT REMOVE. Without this the panel is permanently open.
      // See trap (a) in the header comment.
      '.luma-media__panel[hidden] { display: none !important; }',
      '.luma-media__tabs { display: flex; gap: 6px; margin-bottom: 10px; }',
      '.luma-media__tab {',
      '  font-family: inherit;',
      '  font-size: 10px;',
      '  letter-spacing: 0.08em;',
      '  text-transform: uppercase;',
      '  padding: 5px 10px;',
      '  border: 1px solid #ece7dc;',
      '  border-radius: 3px;',
      '  background: #fff;',
      '  color: #8a8175;',
      '  cursor: pointer;',
      '}',
      '.luma-media__tab.is-active { color: #1C3320; border-color: #C9A456; }',
      '.luma-media__stage {',
      '  position: relative;',
      '  width: 100%;',
      '  max-width: 420px;',
      '  margin: 0 auto;',
      '  aspect-ratio: 1 / 1;',
      '  background: #fff;',
      '  border: 1px solid #ece7dc;',
      '  border-radius: 3px;',
      '  overflow: hidden;',
      '}',
      '.luma-media__photo {',
      '  width: 100%;',
      '  height: 100%;',
      '  object-fit: contain;',
      '  display: block;',
      '}',
      '.luma-media__photo[hidden] { display: none !important; }',
      '.luma-media__360 { position: absolute; inset: 0; }',
      '.luma-media__360[hidden] { display: none !important; }',
      '.luma-media__frame { width: 100%; height: 100%; border: 0; display: block; overflow: hidden; }',
      '.luma-media__hint {',
      '  margin: 8px 0 0;',
      '  font-size: 11px;',
      '  line-height: 1.5;',
      '  color: #8a8175;',
      '  text-align: center;',
      '}',
      '.luma-media__norec {',
      '  margin: 0 0 10px;',
      '  padding: 12px 14px;',
      '  background: #F8F6F1;',
      '  border: 1px solid #ece7dc;',
      '  border-radius: 4px;',
      '}',
      '.luma-media__norec p { margin: 0 0 8px; font-size: 12px; line-height: 1.55; color: #1C3320; }',
      '.luma-media__norec-btn {',
      '  font-family: inherit;',
      '  font-size: 10px;',
      '  letter-spacing: 0.08em;',
      '  text-transform: uppercase;',
      '  padding: 7px 12px;',
      '  border: 1px solid #C9A456;',
      '  border-radius: 3px;',
      '  background: #fff;',
      '  color: #1C3320;',
      '  cursor: pointer;',
      '}',
      '.luma-media__norec-btn:hover { background: #C9A456; color: #fff; }',
      '@media (max-width: 600px) {',
      '  .luma-config__diamond-media.luma-media__trigger { width: 68px; height: 68px; }',
      '}'
    ].join('\n');
    var el = document.createElement('style');
    el.id = STYLE_ID;
    el.appendChild(document.createTextNode(css));
    document.head.appendChild(el);
  }

  /* ---- 6. run ---------------------------------------------------- */
  function tick() {
    try {
      injectStyles();
      syncChoose();
      var cards = root.querySelectorAll('.luma-config__diamond-card');
      for (var i = 0; i < cards.length; i++) decorate(cards[i]);
      maybePromptNoRecommended();
    } catch (e) { /* never break the configurator */ }
  }

  var obs = new MutationObserver(tick);
  obs.observe(root, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tick);
  } else {
    tick();
  }
})();
